import { FieldViewModel } from "../../fields"
import { LayerTypeGroupId, LayerTypeId } from "../../generated/enums"
import { labels } from "../../generated/labels"
import { ISeedRateLayer } from "../../models/seed"
import { str } from "../../strings"
import { LocaleString } from "../../types"
import { createIndex, getDistinctValues, groupBy, isArrayEmpty, partition, selectMany } from "../../utils/array"
import { formatChemicalCompoundInTextAsUnicode } from "../../utils/format"
import { numericTextSort } from "../../utils/numericTextSort"
import { containsText, isDate, isFunction, isString, isUndefinedOrNull, sortDates } from "../../utils/utils"
import { groupTerraMapLayers } from "../terramap"
import { IDataLayer, IDataLayerType } from "../types"
import { isTerraMapLayer } from "../utils"
import { formatLayerNodeName } from "./names"
import { getSatelliteRootNode } from "./satellite"
import { LmNode, MenuNode, NodeLayer, NodesContext } from "./types"

export function getAllNodes(nodes: LmNode[]) {
    return selectMany(nodes.map(n => n.flatten(n => n)))
}

export function getTypesRootNodes<T extends NodeLayer>(context: NodesContext<T>): MenuNode<T>[] {

    const { layers } = context

    return getLayerTypeGroupNodes(context, layers, null)

}

export function getFieldsRootNodes<T extends NodeLayer>(context: NodesContext<T>): MenuNode<T>[] {

    const { fields, layers } = context

    return getFieldsNodes(context, fields, layers, null)

}

export function getSingleFieldRootNodes<T extends NodeLayer>(context: NodesContext<T>, field: FieldViewModel): MenuNode<T>[] {

    const layers = context.layers.filter(l => l.FieldId == field.fieldId)

    return getLayerTypeGroupNodes(context, layers, field)

}

export function getFieldsNodes<T extends NodeLayer>(context: NodesContext<T>, fields: FieldViewModel[], layers: IDataLayer[], type: IDataLayerType): MenuNode<T>[] {

    const tmpl = {
        layerType: type,
        layerTypeGroup: type ? type.GroupId : undefined
    }

    const groups = groupBy(fields, f => f.fieldGroup || "")

    let nodes = (groups.length <= 1)
        ? getFields(fields)
        : groups.map(g => {

            const groupLayers = layers.filter(l => g.values.some(f => f.fieldId == l.FieldId))

            return createNode({
                ...tmpl,
                id: `field-group:${encodeId(g.key)}`,
                text: getFieldGroupText(g.key),
                class: "field-group",
            }, groupLayers, () => getFields(g.values))

        })

    nodes = numericTextSort(nodes, n => n.field?.fieldName ?? n.text)

    if (!(type || context.excludeHoldingLayers)) {

        nodes = [getHoldingNode(context, layers, type), ...nodes]

    }

    return nodes

    ///

    function getFields(fields: FieldViewModel[]) {

        return fields
            .map(f => {

                const fieldLayers = layers.filter(l => l.FieldId == f.fieldId)

                if (type && (fieldLayers.length == 1)) {

                    const n = createLayerNode(context, fieldLayers[0], type, f, layerText => {
                        return type && containsText(type.Name, layerText)
                            ? f.description
                            : f.description + " / " + layerText
                    })

                    n.fieldRoot = true

                    return n

                }

                const getChildren = type
                    ? () => sortFieldLayers(fieldLayers, type).map(l => createLayerNode(context, l, type, f))
                    : () => getLayerTypeGroupNodes(context, fieldLayers, f)

                return createNode({
                    ...tmpl,
                    id: `field:${f.getId()}`,
                    text: getFieldText(f),
                    field: f,
                    class: "field",
                    fieldRoot: true,
                    canShow: type ? true : undefined
                }, fieldLayers, getChildren)

            })
            .sort((a, b) => a.text.localeCompare(b.text))

    }

}

export function getHoldingNode<T extends NodeLayer>(context: NodesContext<T>, layers: IDataLayer[], type: IDataLayerType) {

    type = type || context.layerTypes.find(t => t.TypeId == LayerTypeId.Drawing)

    layers = layers.filter(l => isHoldingLayer(l) && (l.TypeId == type.TypeId))

    return createNode(
        {
            id: "holding",
            text: str.Holding,
            class: "holding",
        },
        layers,
        () => layers
            .map(l => createLayerNode(context, l, type, null))
            .sort((a, b) => a.text.localeCompare(b.text))
    )

}

/**
 * Creates nodes for layer type groups - i.e., each resulting node will map to a type group
 *
 * @param context Group
 * @param layers Layers
 * @param field Field
 */
function getLayerTypeGroupNodes<T extends NodeLayer>(context: NodesContext<T>, layers: IDataLayer[], field?: FieldViewModel): MenuNode<T>[] {

    const [satellite, std] = partition(layers, l => !!l.Satellite)

    let nodes = getStandardGroupNodes(std)

    if (!isArrayEmpty(satellite)) {
        const satelliteNodes = getSatelliteRootNode(context, satellite, field)
        nodes.push(satelliteNodes)
    }

    if (!(field || context.excludeHoldingLayers)) {
        nodes = [getHoldingNode(context, layers, null)].concat(nodes)
    }

    return nodes

    ///


    function getStandardGroupNodes(layers: IDataLayer[]) {

        // Usersnap 2464 - holding layers are included separately
        layers = layers.filter(l => !isHoldingLayer(l))

        const typeIds = createIndex(getDistinctValues(layers, l => l.TypeId), id => id)

        const types = context.layerTypes.filter(lt => !!typeIds[lt.TypeId])

        const groupIds = getDistinctValues(types, t => t.GroupId)

        const groups = getLayerTypeGroups(groupIds, types)

        const nodes = groups.map(g => {

            return g.types.length == 1
                ? getSingleTypeNode(g.types[0], layers)
                : getGroupNode(g)


        })

        return nodes

    }

    function getSingleTypeNode(type: IDataLayerType, layers: IDataLayer[]) {

        return getLayerTypeNode(context, type, layers, field)

    }

    function getGroupNode(group: { id: LayerTypeGroupId, types: IDataLayerType[] }) {

        const types = group.types

        const groupLayers = layers.filter(l => types.some(t => t.TypeId == l.TypeId))

        return createNode({
            id: `type-group:${group.id}`,
            text: getTypeGroupText(group.id),
            field,
            layerTypeGroup: group.id,
            class: "type-group",
            canShow: canShowTypeGroup(group.id)
        }, groupLayers, () => getGroupTypeChildren(group))

    }

    function getGroupTypeChildren(group: { id: LayerTypeGroupId, types: IDataLayerType[] }) {

        const getTypeNodes = (types: IDataLayerType[], layers: IDataLayer[]) => types.map(t => getSingleTypeNode(t, layers))

        switch (group.id) {

            case LayerTypeGroupId.TerraMap:
                return groupTerraMapLayers(group.types, layers)
                    .map(g => createNode({
                        id: g.id,
                        text: g.name,
                        field, layerTypeGroup: group.id
                    }, g.layers, () => getTypeNodes(g.types, g.layers)))

            default:
                return getTypeNodes(group.types, layers)

        }

    }

}

function getLayerTypeNode<T extends NodeLayer>(context: NodesContext<T>, type: IDataLayerType, layers: IDataLayer[], field: FieldViewModel): MenuNode<T> {

    layers = layers.filter(l => l.TypeId == type.TypeId)

    // 220926 - This was used to exclude intermediate type folders when then contain only one layer
    // Those folders will now always be included, to clarify the structure of the explorer
    // if (field && (layers.length == 1)) {
    // 	const n = createLayerNode(context, layers[0], type, field)
    // 	n.showViewers = true
    // 	return n
    // }

    const children = getLayerTypeInfo()

    return createNode({
        id: `type:${type.TypeId}`,
        text: formatChemicalCompoundInTextAsUnicode(type.Name),
        field,
        layerType: type,
        layerTypeGroup: type.GroupId,
        class: "type",
        showViewers: true,
        canSelectChildren: children.canSelectChildren,
        canShow: children.canShow
    }, layers, children.getChildren)

    ///

    function getLayerTypeInfo(): {
        getChildren: () => MenuNode<T>[],
        canSelectChildren: boolean,
        canShow?: boolean | "never"
    } {

        switch (type.TypeId) {

            case LayerTypeId.Biomass:
                const biomassLayers = layers.sort((a, b) => sortDates(b.Date, a.Date))
                return getYearSubgroups(context, type, biomassLayers, field, 1, field ? undefined : l => getDateSubgroups(context, type, l, field))

            case LayerTypeId.Yield:
            case LayerTypeId.YieldAverage:
            case LayerTypeId.YieldTrend:
                return (!field)
                    ? getCropYearSubgroups(context, type, layers, field, 5)
                    : {
                        getChildren: () => getYieldFieldLayers(context, type, layers, field),
                        canSelectChildren: true
                    }

            case LayerTypeId.SeedRate:
                return getPlanSubgroups(context, type, layers, field, l => {
                    const sl = <ISeedRateLayer>l
                    return {
                        planId: String(sl.PlanId),
                        planName: sl.PlanName
                    }
                })

            case LayerTypeId.GenericPlan:
                return getPlanSubgroups(context, type, layers, field, l => {

                    const planId = String(l.GenericPlanId)

                    return {
                        planId,
                        planName: l.GenericPlan?.Name ?? `Plan #${planId}` as LocaleString
                    }

                })

            case LayerTypeId.PrescriptionPlan:
                return getPlanSubgroups(context, type, layers, field, getPrescriptionPlan)

            default:
                return {
                    getChildren: () => getLayerTypeChildren(context, type, layers, field),
                    canSelectChildren: false,
                    canShow: true
                }

        }

    }

}

function getLayerTypeChildren<T extends NodeLayer>(context: NodesContext<T>, type: IDataLayerType, layers: IDataLayer[], field: FieldViewModel): MenuNode<T>[] {

    const children = field
        ? sortFieldLayers(layers, type).map(l => createLayerNode(context, l, type, field))
        : getLayerTypeFieldsNodes(context, type, layers)

    return children

}

function getCropYearSubgroups<T extends NodeLayer>(context: NodesContext<T>, type: IDataLayerType, layers: IDataLayer[], field: FieldViewModel, selectedYears: number) {

    const msgUndefCrop = "(Undefined crop)"

    const groups = groupBy(layers, l => l.Crop, c => c ? c.Name : "")

    const getChildren = () => groups
        .map(g => {

            const cropLayers = g.values

            const crop = g.key
            const cropId = crop ? crop.CropId : 0
            const cropName = crop ? crop.Name : msgUndefCrop

            const years = getYearSubgroups(context, type, cropLayers, field, selectedYears)

            return createNode({
                id: `crop#:${cropId}`,
                text: cropName,
                layerType: type,
                layerTypeGroup: type.GroupId,
                field,
                class: "crop",
                canSelectChildren: years.canSelectChildren,
                canShow: "never"
            }, cropLayers, years.getChildren)

        })

        .sort((a, b) => a.text.localeCompare(b.text))

    return {
        getChildren,
        canSelectChildren: false,
        canShow: "never" as (boolean | "never")
    }

}

function getPlanSubgroups<T extends NodeLayer>(context: NodesContext<T>, type: IDataLayerType, layers: IDataLayer[], field: FieldViewModel, planSelector: (l: IDataLayer) => { planId: string, planName: string }) {

    const plans = layers.map(l => ({
        layer: l,
        ...planSelector(l)
    }))

    const groups = groupBy(plans, p => p.planId)

    const getChildren = () => groups
        .map(p => {

            const planLayers = p.values.map(i => i.layer)

            return createNode({
                id: `plan#:${p.key}`,
                text: p.values[0].planName || "",
                layerType: type,
                layerTypeGroup: type.GroupId,
                field,
                class: "plan"
            }, planLayers, () => getLayerTypeChildren(context, type, planLayers, field))

        })
        .sort((a, b) => a.text.localeCompare(b.text))

    return {
        getChildren,
        canSelectChildren: false
    }

}

function getYearSubgroups<T extends NodeLayer>(
    context: NodesContext<T>,
    type: IDataLayerType,
    layers: IDataLayer[],
    field: FieldViewModel,
    selectedYears: number,
    children?: (layers: IDataLayer[]) => MenuNode<T>[]) {

    const years = groupBy(layers, l => getLayerYear(l, context.year)).sort((a, b) => b.key - a.key)

    const canSelectChildren = years.length > selectedYears

    const selected = years
        .map(g => ({ year: g.key, d: Math.abs(g.key - context.year) }))
        .sort((a, b) => a.d - b.d)
        .filter((i, index) => index < selectedYears)
        .map(i => i.year)

    children = children || (l => getLayerTypeChildren(context, type, l, field))

    const getChildren = () => years.map(y => createNode({
        id: `year#:${y.key}`,
        text: String(y.key),
        layerType: type,
        layerTypeGroup: type.GroupId,
        field,
        class: "year",
        selectedFromParent: canSelectChildren ? selected.some(s => s == y.key) : undefined
    }, y.values, () => children(y.values)))

    return {
        getChildren,
        canSelectChildren,
        canShow: "never" as (boolean | "never")
    }

}

function getDateSubgroups<T extends NodeLayer>(context: NodesContext<T>, type: IDataLayerType, layers: IDataLayer[], field: FieldViewModel) {

    const nullDate = new Date(0)

    const dates = groupBy(layers, l => l.Date || nullDate).sort((a, b) => b.key.valueOf() - a.key.valueOf())

    const getChildren = () => dates.map(d => createNode({
        id: `date#:${d.key}`,
        text: d.key == nullDate ? str.NoDate : d.key.toLocaleDateString(),
        layerType: type,
        layerTypeGroup: type.GroupId,
        field,
        class: "date",
        canShow: true
    }, d.values, () => getLayerTypeChildren(context, type, d.values, field)))

    return getChildren()

}

export function getLayerTypeFieldsNodes<T extends NodeLayer>(context: NodesContext<T>, type: IDataLayerType, layers: IDataLayer[]): MenuNode<T>[] {

    const fieldIds = createIndex(getDistinctValues(layers, l => l.FieldId), id => id)

    const fields = context.fields.filter(f => !!fieldIds[f.fieldId])

    return getFieldsNodes(context, fields, layers, type)

}

function getLayerTypeGroups(ids: LayerTypeGroupId[], types: IDataLayerType[]) {

    return ids.map(id => ({ id, types: types.filter(t => t.GroupId == id) })).sort((a, b) => a.id - b.id)

}

export function createLayerNode<T extends NodeLayer>(context: NodesContext<T>, layer: IDataLayer, type: IDataLayerType, field: FieldViewModel, textProvider?: (layerDescr: string) => string) {

    const vm = context.layerProvider(layer, type, field)

    const name = formatLayerNodeName(vm, type, field)

    const text = isFunction(textProvider) ? textProvider(name) : name

    field = field || getLayerField(context.fields, layer.FieldId, layer.CroppingId)

    return createNode({
        id: `layer:${layer.DataLayerId}`,
        text,
        layer: vm,
        layerType: type,
        field,
        layerTypeGroup: type.GroupId,
        class: "layer"
    }, [layer])

}

export function createNode<T extends NodeLayer>(template: { id: string, text: string } & Partial<MenuNode<T>>, layers: IDataLayer[], subnodes?: (parent: MenuNode<T>) => MenuNode<T>[]): MenuNode<T> {

    let children: MenuNode<T>[]

    const { layer } = template

    const canShow = isUndefinedOrNull(template.canShow)
        ? true
        : template.canShow

    const node: MenuNode<T> = {

        ...template,

        canExpand: isFunction(subnodes),

        expanded: false,
        opacitySlider: false,
        filtered: false,

        canShow,

        getChildren(expand = true): MenuNode<T>[] {
            return expand
                ? children || (children = (subnodes ? subnodes(node) : []))
                : children
        },

        flatten: <U>(selector: (n: MenuNode<T>) => U) => {

            return [selector(node)].concat(children ? selectMany(children.map(c => c.flatten(selector))) : [])

        },

        isVisible: () => {

            if (layer && layer.visible) {
                return true
            }

            return children && children.length && children.some(c => c.isVisible())

        },

    }

    return node

}

function getFieldGroupText(group: string) {
    const msgOtherFields = "Other fields"
    return group || msgOtherFields
}

function getFieldText(field: FieldViewModel) {
    return field.getDescription()
}

function getTypeGroupText(g: LayerTypeGroupId) {
    return labels.LayerTypeGroupId(g)
}

function getLayerField(fields: FieldViewModel[], fieldId: number, croppingId?: number) {

    let field = fields.find(f => f.fieldId == fieldId)

    if (field && croppingId) {
        field = field.croppings.find(c => c.croppingId == croppingId) || field
    }

    return field

}

function encodeId(id: string) {

    // 241125 - DevOps #2908
    // Previous version used btoa() directly, which fails with non Latin1 characters
    // I cannot remember at all why I had decided to base64 the input string at the time,
    // so I am keeping it as is for now, just fixing the encoding issue

    const utf8Bytes = new TextEncoder().encode(id)

    id = btoa(
        Array.from(utf8Bytes)
            .map(byte => String.fromCharCode(byte))
            .join('')
    )

    id = id.replace("/", "|")

    return id

}

function isHoldingLayer(layer: IDataLayer) {
    return layer.HoldingId && (layer.TypeId == LayerTypeId.Drawing)
}

function getLayerYear(l: IDataLayer, defaultYear: number) {
    return l.FirstYearId || (isDate(l.Date) ? l.Date.getFullYear() : 0) || defaultYear
}

function sortFieldLayers(layers: IDataLayer[], type: IDataLayerType) {

    const sortByYearAndDate = () => layers.sort((a, b) =>
        ((b.FirstYearId || 0) - (a.FirstYearId || 0))
        || sortDates(b.Date, a.Date)
    )

    switch (type.TypeId) {

        case LayerTypeId.Yield:
        case LayerTypeId.YieldAverage:
        case LayerTypeId.YieldTrend:
        case LayerTypeId.SoilAnalysis:
        case LayerTypeId.PcnAnalysis:
            return sortByYearAndDate()

        default:

            if (isTerraMapLayer(type)) {
                return sortByYearAndDate()
            }

            return layers

    }


}

function canShowTypeGroup(id: LayerTypeGroupId): boolean | "never" {

    switch (id) {

        case LayerTypeGroupId.Biomass:
        case LayerTypeGroupId.Yield:
            return "never"

        default:
            return undefined

    }

}

/**
 * This is used to pre-select the children layers of a yield + field node
 *
 * It is assumed that the call to "getLayerTypeChildren" will procude of list of nodes sorted by descending harvest date
 *
 * All nodes will be pre-selected if they are:
 * - At most 5 years older from the current year
 * - And at least 10 days older than the previous node
 *
 * This is because there may be multiple maps generated in a couple of consecutive days, specially for fields that have
 * been harvested in multiple jobs over a few days, and more for maps provided by a diret connection to CLAAS
 *
 * By pre-selecting nodes as described below, we aim to display only the latest available map per harvest, by default
 */
function getYieldFieldLayers<T extends NodeLayer>(context: NodesContext<T>, type: IDataLayerType, layers: IDataLayer[], field: FieldViewModel) {

    const limit = 10 * 24 * 60 * 60 * 1000

    const children = getLayerTypeChildren(context, type, layers, field)

    let prev: MenuNode<T> = undefined

    for (const n of children) {

        if (n.layer.visible) {

            n.selectedFromParent = true

        }
        else {

            n.selectedFromParent = (Math.abs(n.layer.year - context.year) <= 5)

            if (n.selectedFromParent && prev) {

                const d1 = prev.layer.date?.valueOf() || 0
                const d2 = n.layer.date?.valueOf() || 0

                if (d1 && d2 && ((d1 - d2) < limit)) {
                    n.selectedFromParent = false
                }

            }

        }

        prev = n

    }

    return children

}



/**
 * Returns the plan id + name of a prescription plan layer
 */
function getPrescriptionPlan(layer: IDataLayer) {

    const data = layer.LayerData

    function isPlan(data: any): data is { Guid: string, PlanName: string } {
        return (typeof (data) === "object") && isString(data["Guid"]) && isString(data["PlanName"])
    }

    if (isPlan(data)) {
        return {
            planId: data.Guid,
            planName: data.PlanName
        }
    }

    return {
        planId: "?",
        planName: "(Unknown plan)"
    }

}
